import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, User, Wrench, Leaf, ShieldCheck } from "lucide-react";
import { SITE, IMAGES, API_URL } from "@/lib/site";
import { Reveal, SectionHead } from "@/components/motion";
import { useSEO } from "@/components/SEO";

const VALUES = [
  {
    icon: ShieldCheck, title: "Honnêteté",
    text: "On ne répare que ce qui doit l'être, et on vous explique pourquoi. Le reste peut attendre — on vous le dit aussi.",
  },
  {
    icon: Wrench, title: "Savoir-faire",
    text: "La mécanique est un métier de précision. Chaque véhicule, de la voiture familiale au scooter quotidien, reçoit la même attention.",
  },
  {
    icon: Leaf, title: "Responsabilité",
    text: "Huiles usagées et pièces remplacées sont éliminées selon les filières de recyclage en vigueur en Suisse.",
  },
];

export default function APropos() {
  useSEO(
    "À propos — Atelier mécanique indépendant à Prilly | BM MECHANICS",
    "Découvrez BM MECHANICS, atelier de mécanique indépendant à Prilly, Ouest lausannois. Un artisan mécanicien, une parole donnée."
  );

  const [portrait, setPortrait] = useState(null);
  const [bio, setBio] = useState("");
  useEffect(() => {
    fetch(`${API_URL}/photos`)
      .then((r) => r.json())
      .then((d) => {
        const p = Array.isArray(d) && d.find((x) => x.role === "portrait");
        if (p) setPortrait(p.data);
      })
      .catch(() => {});
    fetch(`${API_URL}/contenu`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.settings?.bio_text) setBio(d.settings.bio_text);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="apropos-page">
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-16 md:pt-40 md:pb-20 texture-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">À propos</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              Un atelier indépendant,<br />une parole donnée
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="apropos-histoire">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHead index="01" eyebrow="L'histoire" title="La mécanique, un métier de passion" />
            <Reveal delay={0.1}>
              <div className="space-y-4 text-sm sm:text-base leading-relaxed text-[#4A4D53]">
                {bio ? (
                  <p className="whitespace-pre-line">{bio}</p>
                ) : (
                  <>
                    <p>
                      BM MECHANICS est né d'une conviction simple : la mécanique doit être
                      un métier de confiance. Installé à Prilly, dans l'Ouest lausannois,
                      l'atelier accueille les voitures, motos et scooters des habitants
                      du quartier et des communes voisines — Lausanne, Renens, Chavannes
                      et au-delà.
                    </p>
                    <p>
                      Ici, vous ne parlez ni à un standard ni à un vendeur : vous parlez
                      directement à la personne qui met les mains dans votre moteur.
                      Chaque diagnostic est expliqué, chaque devis est détaillé, chaque
                      pièce remplacée peut vous être montrée.
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
                      Texte de présentation personnelle — modifiable depuis l'espace atelier
                    </p>
                  </>
                )}
                <p className="border-l-2 border-[#8C7B5D] pl-4 italic text-[#121212]">
                  « Un client satisfait est un client fidèle — et notre meilleure publicité. »
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div
              className={`aspect-[4/5] max-w-md flex flex-col items-center justify-center gap-4 text-center ${
                portrait ? "border border-[#D8D4CA] bg-white p-2" : "border border-dashed border-[#B7B2A4] bg-white/50 p-8"
              }`}
              data-testid="apropos-photo-placeholder"
            >
              {portrait ? (
                <img src={portrait} alt="Le mécanicien de BM MECHANICS à Prilly" className="w-full h-full object-cover" />
              ) : (
                <>
                  <span className="flex h-16 w-16 items-center justify-center bg-[#121212] text-[#8C7B5D] rounded-full">
                    <User className="h-7 w-7" />
                  </span>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9A968C]">
                    Portrait du mécanicien
                  </p>
                  <p className="text-sm text-[#9A968C] max-w-xs">
                    Emplacement réservé à la photo et à la présentation personnelle du fondateur de l'atelier.
                  </p>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#EFECE4] hairline-t hairline-b" data-testid="apropos-valeurs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="02" eyebrow="Valeurs" title="Ce qui guide chaque intervention" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div data-testid={`valeur-${i}`} className="h-full bg-white border border-[#D8D4CA] p-7 hover:border-[#121212] transition-colors">
                  <v.icon className="h-6 w-6 text-[#8C7B5D]" />
                  <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-[#121212]">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A4D53]">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="apropos-atelier">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="03" eyebrow="L'atelier" title="Un espace de travail à la hauteur" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[IMAGES.atelierReel, IMAGES.vitrineAutoFace, IMAGES.vitrineMoto].map((src, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group border border-[#D8D4CA] bg-white p-2 hover:border-[#121212] transition-colors">
                  <div className="overflow-hidden">
                    <img
                      src={src}
                      alt={`Atelier BM MECHANICS — photo ${i + 1}`}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9A968C]">
            Le vrai atelier et ses véhicules — photos BM MECHANICS
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#121212] text-[#F6F3EB] texture-grain" data-testid="apropos-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
              Venez rencontrer l'atelier
            </h2>
            <p className="mt-4 max-w-lg mx-auto text-sm leading-relaxed text-[#9A968C]">
              Un doute sur votre véhicule ? Appelez ou passez nous voir à Prilly — le conseil est gratuit, le café est offert.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.phoneHref}
                data-testid="apropos-call-btn"
                className="inline-flex items-center gap-2 bg-[#8C7B5D] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] font-medium px-7 py-4 hover:bg-[#F6F3EB] transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
              <Link
                to="/devis"
                data-testid="apropos-quote-btn"
                className="inline-flex items-center gap-2 border border-[#4A4D53] font-mono text-xs uppercase tracking-[0.16em] px-7 py-4 hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
              >
                Demander un devis <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
